/**
 * Single entry point for the Intercom messenger.
 *
 * The messenger no longer owns a launcher of its own: it boots with
 * `hide_default_launcher: true` and is opened from the Lodgeic assistant dock,
 * so the bottom-right corner reads as one control with the AI as its primary
 * action. Every boot goes through here precisely so that flag cannot drift —
 * four separate call sites used to pass their own settings object, and a single
 * one of them forgetting the flag brings Intercom's bubble back next to ours.
 *
 * It also fixes a gap that only becomes visible once support lives inside our
 * dock: every boot site was a LOGIN path, so a signed-in user who refreshed the
 * page had no booted messenger at all. A dock item that silently does nothing is
 * worse than no dock item, hence ensureIntercomBooted().
 */

import Intercom, { hide, onHide, onShow, onUnreadCountChange, show, showNewMessage } from '@intercom/messenger-js-sdk';
import { fetchUserAttributes } from 'aws-amplify/auth';

export const INTERCOM_APP_ID = 'hae2ajlm';

/**
 * How long to wait for the authoritative boot (useUserSettings, which mints the
 * identity JWT) before booting a weaker one from a fallback path.
 */
const BOOT_GRACE_MS = 5000;
const BOOT_POLL_MS = 250;

let booted = false;
let bootedWithJwt = false;
let bootInFlight = null;

/** The SDK warns to the console when a method is called before boot. */
function messengerAvailable() {
  return typeof window !== 'undefined' && typeof window.Intercom === 'function';
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Boot the messenger for a known user. Idempotent: the SDK itself only honours
 * the first call, so repeat calls from the various login paths are harmless but
 * pointless, and this returns early to keep that explicit.
 */
export function bootIntercom({ userId, email, name, jwt } = {}) {
  if (!email && !userId) return;
  // A JWT-less boot must NOT lock out the JWT boot that follows it. The identity
  // token is minted asynchronously in useUserSettings, which therefore calls this
  // second; a plain `if (booted) return` made that call a no-op and the messenger
  // stayed unverified. With identity verification enforced on the workspace an
  // unverified messenger silently refuses to open, so clicking support did
  // nothing at all (observed 2026-08-17: hide_default_launcher was applied,
  // intercom_user_jwt was absent, Intercom('show') produced no messenger frame).
  //
  // The SDK's init() applies later settings through Intercom('update') when the
  // widget is already loaded, so re-calling with the JWT upgrades the live
  // messenger in place.
  if (booted && (bootedWithJwt || !jwt)) return;
  Intercom({
    app_id: INTERCOM_APP_ID,
    user_id: userId || email,
    name: name || undefined,
    email: email || undefined,
    // Identity verification, when the caller managed to mint a JWT. Callers that
    // fail to get one fall back to an unauthenticated boot, so this stays optional.
    ...(jwt ? { intercom_user_jwt: jwt } : {}),
    // The Lodgeic dock is the launcher. Without this the messenger draws its own
    // bubble in the same corner and the two launchers stack.
    hide_default_launcher: true,
  });
  // Flagged only after the call lands, so a throwing boot can still be retried
  // by a caller's fallback path (useUserSettings boots again without its JWT).
  booted = true;
  bootedWithJwt = bootedWithJwt || !!jwt;
}

/**
 * Wait for a messenger, and boot one only as a last resort.
 *
 * `useUserSettings` is the authoritative booter: it runs on every authenticated
 * settings load (so it covers refresh) and it is the only caller that carries the
 * identity JWT. This must therefore NOT race ahead of it — booting first without
 * a token is what left the messenger unverified and unopenable. It waits out a
 * grace period, and falls back to an unverified boot only if nothing arrives,
 * which is still better than no support channel at all.
 */
export async function ensureIntercomBooted({ graceMs = BOOT_GRACE_MS } = {}) {
  if (isIntercomBooted()) return true;
  if (bootInFlight) return bootInFlight;
  bootInFlight = (async () => {
    try {
      const deadline = Date.now() + graceMs;
      while (Date.now() < deadline) {
        await sleep(BOOT_POLL_MS);
        if (isIntercomBooted()) return true;
      }
      const attributes = await fetchUserAttributes();
      if (!attributes?.email) return false;
      bootIntercom({
        userId: attributes.email,
        email: attributes.email,
        name: attributes.given_name,
      });
      return isIntercomBooted();
    } catch {
      // Not signed in, or no session yet. The dock hides its support item.
      return false;
    } finally {
      bootInFlight = null;
    }
  })();
  return bootInFlight;
}

/**
 * A messenger exists and is reachable. `window.intercomSettings` counts too, so
 * a boot from any path (not just this module) is recognised.
 */
export function isIntercomBooted() {
  if (!messengerAvailable()) return false;
  return booted || !!(window.intercomSettings && window.intercomSettings.app_id);
}

/** Open the messenger. Returns false when there is nothing to open. */
export function showIntercom() {
  if (!messengerAvailable()) return false;
  show();
  return true;
}

/**
 * Open the messenger on a NEW conversation with the composer pre-filled. The
 * user reviews and sends it themselves — nothing is sent for them.
 */
export function showIntercomNewMessage(text) {
  if (!messengerAvailable()) return false;
  showNewMessage(text || '');
  return true;
}

export function hideIntercom() {
  if (!messengerAvailable()) return;
  hide();
}

/**
 * Mirror the messenger's unread count onto our own dock item — hiding
 * Intercom's launcher also hides its unread badge, and an unanswered reply the
 * user never sees is the whole cost of taking the launcher over.
 *
 * Returns a no-op unsubscribe: Intercom's callback registration has no removal
 * API, so the callback checks a live flag instead of being detached.
 */
export function subscribeUnreadCount(callback) {
  if (!messengerAvailable()) return () => {};
  let active = true;
  onUnreadCountChange((count) => {
    if (active) callback(Number(count) || 0);
  });
  return () => { active = false; };
}

/** Track whether the messenger panel itself is open, to reflect it in the dock. */
export function subscribeMessengerVisibility(callback) {
  if (!messengerAvailable()) return () => {};
  let active = true;
  onShow(() => { if (active) callback(true); });
  onHide(() => { if (active) callback(false); });
  return () => { active = false; };
}
